import { useQuery } from '@tanstack/react-query';
import { useEffect, useId, useMemo, useState, type KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { paths } from '@/app/paths';
import { Icon, IconTile, Modal, type IconName } from '@/design-system';
import { cx } from '@/lib/cx';
import { errorText } from '@/shell/errorText';
import { MIN_QUERY, searchQuery } from './api';
import s from './search.module.css';

type Hit = { key: string; group: 'organizations' | 'websites' | 'domains'; icon: IconName; title: string; sub: string; to: string; mono?: boolean };

/**
 * The header search (P5-E06, additive): «Организация, сайт или домен» as a command palette in the design's
 * language — the scrim, a card with the 48px search pill, caps group labels and 38px icon-tile rows that
 * hover to #1C1C1F. ⌘K / Ctrl+K or a click on the pill opens it; arrows move, Enter opens, Esc closes.
 */
export function CommandPalette({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { t } = useTranslation('search');
  const { t: ts } = useTranslation('sites');
  const { t: tc } = useTranslation('common');
  const navigate = useNavigate();
  const listId = useId();
  const [text, setText] = useState('');
  const [q, setQ] = useState('');
  const [active, setActive] = useState(0);
  useEffect(() => {
    const id = setTimeout(() => setQ(text.trim()), 200);
    return () => clearTimeout(id);
  }, [text]);
  const res = useQuery({ ...searchQuery(q), enabled: open && q.length >= MIN_QUERY });
  const data = q.length >= MIN_QUERY ? res.data : undefined;

  const hits = useMemo<Hit[]>(() => {
    if (!data) return [];
    return [
      ...data.organizations.map((o): Hit => ({ key: `o-${o.id}`, group: 'organizations', icon: 'building-2', title: o.brand_name, sub: o.legal_name ?? o.industry ?? '', to: paths.organization(o.id) })),
      ...data.websites.map((w): Hit => ({ key: `w-${w.id}`, group: 'websites', icon: 'layout-panel-left', title: w.name, sub: `${w.organization.brand_name} · ${ts(`status.${w.status}`)}`, to: paths.site(w.id) })),
      ...data.domains.map((d): Hit => ({ key: `d-${d.hostname}`, group: 'domains', icon: 'globe', title: d.hostname, sub: `${d.website.name} · ${d.organization.brand_name}`, to: paths.site(d.website.id, 'domains'), mono: true })),
    ];
  }, [data, ts]);

  const close = () => {
    setText('');
    setQ('');
    setActive(0);
    onClose();
  };
  const go = (h: Hit | undefined) => {
    if (!h) return;
    navigate(h.to);
    close();
  };
  const onKey = (e: KeyboardEvent<HTMLInputElement>) => {
    if (!hits.length) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((i) => (i + 1) % hits.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((i) => (i - 1 + hits.length) % hits.length);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      go(hits[Math.min(active, hits.length - 1)]);
    }
  };
  const current = Math.min(active, Math.max(0, hits.length - 1));
  const optionId = (i: number) => `${listId}-${i}`;
  let index = -1;
  return (
    <Modal open={open} onClose={close} variant="sheet" label={t('label')} className={s.panel}>
      <div className={s.field}>
        <Icon name="search" size={16} className={s.fieldIcon} />
        <input
          className={s.input}
          role="combobox"
          aria-expanded={hits.length > 0}
          aria-controls={listId}
          aria-activedescendant={hits.length ? optionId(current) : undefined}
          aria-autocomplete="list"
          aria-label={t('label')}
          placeholder={t('placeholder')}
          value={text}
          data-autofocus
          onChange={(e) => {
            setText(e.target.value);
            setActive(0);
          }}
          onKeyDown={onKey}
        />
        <span className={s.esc}>{t('esc')}</span>
      </div>
      <div id={listId} role="listbox" aria-label={t('results')} className={s.results}>
        {text.trim().length < MIN_QUERY ? (
          <div className={s.note}>{t('hint')}</div>
        ) : res.isError ? (
          <div className={s.note}>{errorText(res.error, tc)}</div>
        ) : data && !hits.length ? (
          <div className={s.note}>{t('empty', { q })}</div>
        ) : (
          (['organizations', 'websites', 'domains'] as const).map((group) => {
            const rows = hits.filter((h) => h.group === group);
            if (!rows.length) return null;
            return (
              <div key={group} role="group" aria-label={t(`groups.${group}`)} className={s.group}>
                <span className={s.groupLabel} aria-hidden>
                  {t(`groups.${group}`)}
                </span>
                {rows.map((h) => {
                  index += 1;
                  const i = index;
                  return (
                    <div
                      key={h.key}
                      id={optionId(i)}
                      role="option"
                      aria-selected={i === current}
                      className={cx(s.row, i === current && s.rowActive)}
                      onMouseEnter={() => setActive(i)}
                      onClick={() => go(h)}
                    >
                      <IconTile icon={h.icon} size={38} iconSize={17} />
                      <div className={s.rowText}>
                        <span className={cx(s.rowTitle, h.mono && s.mono)}>{h.title}</span>
                        {h.sub && <span className={s.rowSub}>{h.sub}</span>}
                      </div>
                      <Icon name="chevron-right" size={16} className={s.chevron} />
                    </div>
                  );
                })}
              </div>
            );
          })
        )}
      </div>
    </Modal>
  );
}

/** ⌘K / Ctrl+K anywhere opens the palette (and closes it again). */
export function usePaletteShortcut(toggle: () => void): void {
  useEffect(() => {
    const onKey = (e: globalThis.KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && !e.altKey && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        toggle();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [toggle]);
}
